export default function InheritedLandSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/589e994f1b10136ee760ea73a7107f28.jpg"
              alt="How to sell inherited land"
              className="w-full h-full object-cover"
            />
          </div>

          <div>
            <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold mb-6">
              Inherited Property Solutions
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
              How to Sell Inherited Land: A Complete Guide
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Inheriting land can feel overwhelming, especially when you don't want the property or can't afford to maintain it. Whether you've inherited vacant land from a parent, grandparent, or other relative, you're facing decisions about probate, taxes, and what to do with property you may have never even seen.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The good news: selling inherited land for cash is often the simplest solution. Here's everything you need to know about the process and how TRex RE LLC can help you navigate it.
            </p>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">Step-by-Step: Selling Land in Probate</h3>
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
              <h4 className="text-xl font-bold mb-3 text-gray-900">Step 1: Determine Probate Requirements</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                In Texas, if the deceased left a will, the property must go through probate court before you can sell. If there's no will (intestate), the court will determine rightful heirs. This process typically takes 6-12 months but can be expedited with proper legal guidance.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Small estates under $75,000 may qualify for simplified probate procedures. An attorney can help you determine the fastest path forward. We work with probate attorneys and can provide referrals if needed.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
              <h4 className="text-xl font-bold mb-3 text-gray-900">Step 2: Get Court Approval to Sell</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Once you're appointed as executor or administrator, you'll need court permission to sell the property. This involves filing a petition with the probate court and potentially getting the property appraised.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Having a cash buyer ready (like TRex RE LLC) can expedite this process. Courts prefer quick sales that convert assets to cash for estate distribution, especially when there are multiple heirs or outstanding debts.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
              <h4 className="text-xl font-bold mb-3 text-gray-900">Step 3: Handle Multiple Heirs</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                If you're one of several heirs, all parties must agree to the sale. This can be complicated when siblings or relatives disagree about keeping versus selling the property. A cash sale often provides the cleanest solution - quick liquidity that can be divided according to the will or state law.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We've helped dozens of families navigate multi-heir situations. Our straightforward cash offers eliminate arguments about listing prices, realtor selection, and waiting for buyers.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
              <h4 className="text-xl font-bold mb-3 text-gray-900">Step 4: Address Estate Debts and Liens</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Inherited land often comes with baggage: unpaid property taxes, mortgages, or liens. As executor, you're responsible for paying these debts from estate assets before distributing to heirs.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We can structure our purchase to pay off liens directly at closing, simplifying the process. Even if the land has significant debt, we can often make it work so heirs still receive something rather than losing the property to foreclosure or tax sale.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-teal-600">
              <h4 className="text-xl font-bold mb-3 text-gray-900">Step 5: Understand Tax Implications</h4>
              <p className="text-gray-700 leading-relaxed mb-3">
                Good news: inherited property receives a "stepped-up basis," meaning the property's value is reset to its fair market value at the date of death. This often eliminates or significantly reduces capital gains taxes when you sell.
              </p>
              <p className="text-gray-700 leading-relaxed">
                For example, if your parent bought land for $10,000 in 1980 and it's worth $50,000 when they pass, your basis is $50,000. If you sell for $50,000, you owe no capital gains tax. Always consult a tax professional, but this stepped-up basis is a major advantage of inherited property.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">Why Sell Inherited Land for Cash?</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-2xl mt-1"></i>
              <div>
                <strong className="block mb-1">Avoid Ongoing Costs</strong>
                <span className="text-teal-50">Stop paying property taxes, insurance, and maintenance on land you don't want</span>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-2xl mt-1"></i>
              <div>
                <strong className="block mb-1">Simplify Estate Settlement</strong>
                <span className="text-teal-50">Convert property to cash for easy distribution among heirs</span>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-2xl mt-1"></i>
              <div>
                <strong className="block mb-1">Eliminate Family Disputes</strong>
                <span className="text-teal-50">Cash offers provide clear, fair value that all parties can agree on</span>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <i className="ri-check-line text-2xl mt-1"></i>
              <div>
                <strong className="block mb-1">Close Quickly</strong>
                <span className="text-teal-50">Once probate is complete, close in as little as 7 days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
